import { useCallback } from "react";

import { AvailablePaymentCurrency } from "@simplyk/common";

import { trpc } from "@/helpers/trpc";
import { parseAsStringLiteral, useQueryState } from "@/hooks/useQueryState";

const PAGE_SIZE = 20;

export const useThemedDonorListDialogState = () => {
  const [donorsQuery, setDonorsQuery] = useQueryState(
    "donors",
    parseAsStringLiteral(["view-all"] as const).withOptions({ history: "push" })
  );

  const onOpen = useCallback(() => {
    setDonorsQuery("view-all");
  }, [setDonorsQuery]);

  const onClose = useCallback(() => {
    setDonorsQuery(null);
  }, [setDonorsQuery]);

  return {
    open: donorsQuery === "view-all",
    onOpen,
    onClose,
  };
};

export const useThemedDonorListDialogServerState = ({
  donationFormId,
  open,
}: {
  donationFormId: string;
  open: boolean;
}) => {
  const {
    isFetching,
    isFetchingNextPage,
    data,
    hasNextPage: hasMoreDonors,
    fetchNextPage: onFetchMoreDonors,
  } = trpc.form_getLatestDonations.useInfiniteQuery(
    {
      formId: donationFormId,
      limit: PAGE_SIZE,
    },
    {
      enabled: open,
      initialCursor: 0,
      getNextPageParam: (lastPage) => lastPage?.nextCursor,
    }
  );

  const donors =
    data?.pages
      ?.filter((x) => !!x)
      ?.map((x) => x!.items)
      ?.flat()
      ?.map((x) => ({
        fullName: x.fullName,
        donatedOn: new Date(x.donatedOn),
        donationAmount: x.amount,
        donationCurrency: x.currency as AvailablePaymentCurrency,
      })) ?? [];

  return {
    isLoading: isFetching && !isFetchingNextPage,
    donors,
    hasMoreDonors: hasMoreDonors ?? false,
    onFetchMoreDonors,
  };
};
